// SaloonDirector: the client side of the saloon stage (sim/saloon.ts). It owns everything that
// happens before the island and after it: boarding the car, taking a seat (any free stool or
// booth), talking to Pop from a bar stool, the gun rack, the ready-up, the roof-blast launch
// cinematic, the drop + superhero landing, and the hotdog scene on a wipe.
//
// Talking to Pop is a conversation, not a menu: you sit at the bar, Pop comes down the bar to you
// and says something, and a handful of short replies come up under his line. Pick one and your
// cowboy says it, Pop answers. Ask about kit and Pop points at the rack by the door; the camera goes
// over to it while you stay on the stool, and the rack's readout (rackui.ts) lets you browse class,
// guns and grenades with the item itself lit up on the wall.
//
// GameClient hands it the frame (own cowboy, the cowboy render states, the camera) and it bends
// them: filters input while seated or while a scene is playing, takes the camera for the seat, the
// conversation, the rack and the cinematics, sets CowboyRS.scripted for the sit / eat / brace /
// landing poses, and dresses the lights and post for the indoor room. All local per peer except
// who sits where (C2S.Sit / Ev.Seat) and the ready-up: the server only sends the stage.

import * as THREE from 'three';
import { Btn, InputFrame, Move, PlayerState } from '../../sim/player';
import { CAR, DROP_HEIGHT, LAUNCH_SECS, NO_SEAT, RunStage, SALOON, SALOON_STRIP, SEATS, SaloonReason, inSaloon, launchSpot, wipeStool } from '../../sim/saloon';
import { ClassId } from '../../sim/defs';
import type { Loadout } from '../../sim/weapons';
import type { CowboyRS, FxApi, SfxApi } from '../render/contracts';
import type { Stage } from '../render/stage';
import type { NetSession } from '../net/session';
import type { Input } from '../input/input';
import { SaloonCar } from './car';
import { Pop } from './pop';
import { Hotdog } from './hotdog';
import { SaloonUI, type Hint, type Row } from './ui';
import { RackUI, type RackKind } from './rackui';
import { firstPad } from '../input/padnav';
import * as L from './lines';

/** The Service Record (client/progress/record.ts) registers itself here: Pop keeps the paperwork. */
export const paperwork: { open: ((onClose: () => void) => void) | null } = { open: null };

/** how far under the cushion the cowboy's origin goes while seated (the sit pose keeps the hips at
 *  about standing height over the origin) */
const SEAT_DROP = 0.83;
const SEAT_REACH = 1.35; // how close to a seat you need to be to take it
const RACK_R = 1.6;
const HOTDOG_SECS = 9.5; // the cinematic part of the wipe return
const HOTDOG_LOCK = 8.7; // no getting up mid-bite
/** the hotdog, in seconds after the wipe return: the hand reaches for the plate, has it, holds it up */
const EAT_REACH = 1.3, EAT_GRAB = 1.85, EAT_HELD = 2.55;
/** the top of each bite (the hand at the mouth); then it goes back down on the plate */
const EAT_BITES = [3.0, 4.6, 6.2];
const EAT_UP = 0.45, EAT_DOWN = 0.5, EAT_BACK = 7.4, EAT_DONE = 8.1;
const DROP_CAM_SECS = 4.5;
const SIT_BLEND = 0.35;

const v = new THREE.Vector3();
const Y_UP = new THREE.Vector3(0, 1, 0);
const v2 = new THREE.Vector3();
const _box = new THREE.Box3();
const _m4 = new THREE.Matrix4();
const _hq = new THREE.Quaternion(), _hs = new THREE.Vector3();
const smooth = (t: number) => { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); };
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
/** car-local → world */
const wx = (x: number) => SALOON.x + x, wy = (y: number) => SALOON.y + y, wz = (z: number) => SALOON.z + z;

export interface KitApi {
  cls(): ClassId;
  loadout(cls: ClassId): Loadout;
  setClass(c: ClassId): void;
  setLoadout(cls: ClassId, l: Loadout): void;
}

export interface DirectorDeps {
  stage: Stage;
  session: NetSession;
  input: Input;
  sfx: SfxApi;
  fx: () => FxApi | null;
  shake: (amount: number) => void;
  quality: number;
  /** hide the island world while we're indoors (the windows look out on space, and it's free frame time) */
  setWorldVisible?: (on: boolean) => void;
  /** class + loadout picks at the rack */
  kit: KitApi;
  /** a cowboy's bone as drawn this frame (the hotdog rides the right hand) */
  boneWorld?: (id: number, bone: 'RightHand' | 'RightForeArm' | 'Head', out: THREE.Matrix4) => boolean;
  /** your cowboy's name for your side of the conversation */
  myName?: () => string;
}

type Mode = 'walk' | 'seated' | 'talk' | 'rack' | 'paper';
interface Sitter { seat: number; since: number; fx: number; fy: number; fz: number; fyaw: number; captured: boolean }

export class SaloonDirector {
  stageNow: RunStage = RunStage.Saloon;
  reason: SaloonReason = SaloonReason.Board;
  /** seconds since the current stage began (local clock) */
  t = 0;
  readyN = 0; readyNeed = 1; myReady = false;
  /** post-pass requests the game folds in (max'd with its own) */
  postFx = { desat: 0, flash: 0 };
  readonly car: SaloonCar;
  readonly pop: Pop;
  readonly ui = new SaloonUI();
  private rackUI = new RackUI();
  private hotdog: Hotdog;
  private d: DirectorDeps;
  private prevButtons = 0;
  private mode: Mode = 'walk';
  /** the seat you'd take with □ (-1 none), or 'rack' standing at the rack */
  private nearSeat = -1;
  private nearRack = false;
  private sitAsked = -10;
  private pushT = 0;
  /** the stick has come back to centre since you sat (a stick still held from walking up mustn't stand you
   *  straight back up: that's how you'd hop from stool to stool) */
  private stickArmed = false;
  /** right-stick peek while the conversation camera has you (yaw, pitch in radians), smoothed */
  private peek = { yaw: 0, pitch: 0 };
  /** who's sitting where, and who just stood up (for the blend back to standing) */
  private sitters = new Map<number, Sitter>();
  private leavers = new Map<number, { seat: number; since: number }>();
  private jobIx = 0;
  // the conversation
  private popLineUntil = 0;
  private queued: { at: number; run: () => void } | null = null;
  private talkShot: 'pop' | 'you' = 'pop';
  private rackBack: Mode = 'walk';
  private rackFrom = 0;
  /** drop: local time the drop began, when we landed */
  private dropAt = -1; private landedAt = -1; private sawAir = false;
  private lit = { sun: 0, hemi: 0, sunColor: new THREE.Color(), hemiSky: new THREE.Color(), hemiGround: new THREE.Color(), hud: 1, shafts: 0, saved: false };
  // the director's camera: smoothed toward the shot it wants, blended over the game's own camera
  private camPos = new THREE.Vector3();
  private camLook = new THREE.Vector3();
  private camK = 0;
  private camCut = true;
  private wantPos = new THREE.Vector3();
  private wantLook = new THREE.Vector3();
  private boarded = false;
  private rumble = 0;
  private mutterAt = 25;
  private launchSaid = false;
  private breached = false;
  private flashFired = false;
  private hotdogSaid = false;
  private welcomed = false;
  private picked = false;
  private bitesDone = 0;
  private klaxon = false;
  private ignited = false;
  private handM = new THREE.Matrix4();
  private headM = new THREE.Matrix4();
  private headP = new THREE.Vector3();
  private elbowM = new THREE.Matrix4();
  private wristP = new THREE.Vector3();
  private elbowP = new THREE.Vector3();
  private mouthP = new THREE.Vector3();
  private camMouth = new THREE.Vector3();
  private camMouthT = -1;
  private plateP = new THREE.Vector3();
  private holdP = new THREE.Vector3();
  private biteP = new THREE.Vector3();
  /** this frame's eat pose (the local cowboy's right hand), from eatPose() */
  private eat = { w: 0, lift: 0, dip: 0, fist: 0, x: 0, y: 0, z: 0 };

  constructor(d: DirectorDeps) {
    this.d = d;
    this.car = new SaloonCar(d.stage.renderer, d.quality);
    this.pop = new Pop();
    this.car.group.add(this.pop.group);
    this.hotdog = new Hotdog(this.car.group);
    d.stage.renderer.localClippingEnabled = true; // the bites out of the hotdog
    d.stage.scene.add(this.car.group);
    this.ui.onType = () => { this.d.sfx.play('ui_move', undefined, { volume: 0.12, pitch: 1.6 + Math.random() * 0.4 }); };
    this.ui.onMove = () => this.d.sfx.play('ui_move', undefined, { volume: 0.4 });
    this.ui.onPick = () => this.d.sfx.play('ui_select', undefined, { volume: 0.5 });
    this.applyLook(true);
    this.car.setIndoor(true);
    d.setWorldVisible?.(false);
  }

  dispose() { this.ui.dispose(); this.rackUI.dispose(); this.car.group.removeFromParent(); }

  /** true while the run's island isn't the place to be (saloon or launch) */
  get aboard() { return this.stageNow !== RunStage.Run; }
  /** true while a scene owns the screen (no HUD, no combat camera) */
  get cinematic() {
    return this.stageNow === RunStage.Launch || (this.stageNow === RunStage.Run && this.dropAt >= 0 && this.landedAt < 0)
      || this.hotdogPlaying;
  }
  get hotdogPlaying() { return this.stageNow === RunStage.Saloon && this.reason === SaloonReason.Wipe && this.t < HOTDOG_SECS; }

  private get myId() { return this.d.session.me?.id ?? this.d.session.pid; }
  private mySeat() { return this.sitters.get(this.myId)?.seat ?? -1; }

  // ---------------------------------------------------------------- server events
  onStage(stage: RunStage, reason: SaloonReason, ticksIn: number) {
    const fresh = stage !== this.stageNow || ticksIn === 0;
    const was = this.stageNow;
    const busy = this.mode === 'talk' || this.mode === 'rack' || this.mode === 'paper';
    this.stageNow = stage; this.reason = reason;
    if (fresh) this.t = ticksIn / 60;
    this.endTalk(true);
    this.closeRack();
    this.sitters.clear(); this.leavers.clear();
    this.mode = 'walk';
    // the stage moved on while you were talking to Pop or at the rack (the posse saddled up): closing
    // the rack hands you back to Pop with input still held, so let go of it here or you ride out frozen
    if (busy) { this.queued = null; this.ui.closeChoices(); this.d.input.blocked = false; this.d.input.swallowPad(); }
    if (stage === RunStage.Saloon) {
      this.myReady = false;
      this.car.breach(false); this.breached = false;
      this.dropAt = -1; this.landedAt = -1;
      if (reason === SaloonReason.Wipe && ticksIn < 30) {
        // everyone is back on a stool at the bar (the server seats them), the frank on the plate in front
        this.hotdogSaid = false; this.welcomed = false; this.picked = false; this.bitesDone = 0; this.camMouthT = -1;
        const s = SEATS[wipeStool(this.d.session.slot)];
        this.hotdog.set(true, CAR.barX0 + 0.24, CAR.barTop, s.z);
        this.pop.homeZ = popSpot(s.z);
        this.ui.fade(1);
      } else {
        this.hotdog.set(false);
        this.pop.homeZ = CAR.popZ;
        if (reason === SaloonReason.Extract && was === RunStage.Run) this.popCaption(L.next(L.VISIT_FLAVOR), 3.2);
        this.d.input.yaw = 0; this.d.input.pitch = -0.05;
      }
    } else if (stage === RunStage.Launch) {
      this.launchSaid = false; this.flashFired = false; this.klaxon = false; this.ignited = false;
      if (fresh && ticksIn < 30) this.d.sfx.cue?.('sendoff');
      this.hotdog.set(false);
      this.pop.homeZ = CAR.popZ;
      this.ui.setPrompt(null, ''); this.ui.setHints(null);
    } else if (stage === RunStage.Run) {
      this.dropAt = was === RunStage.Launch || ticksIn < 30 ? 0 : -1;
      this.landedAt = -1; this.sawAir = false;
      this.car.breach(false);
      if (this.dropAt === 0) {
        this.d.input.pitch = -0.95;
        this.ui.fade(1);
      }
    }
  }

  onReady(n: number, need: number) {
    const before = this.readyN;
    this.readyN = n; this.readyNeed = Math.max(1, need);
    if (n < before && this.myReady && n === 0) this.myReady = false;
  }

  /** someone sat down (seat) or stood up (NO_SEAT) */
  onSeat(pid: number, seat: number) {
    const had = this.sitters.get(pid);
    if (seat === NO_SEAT || seat >= SEATS.length) {
      if (had) { this.sitters.delete(pid); this.leavers.set(pid, { seat: had.seat, since: this.t }); }
      if (pid === this.myId) { this.endTalk(true); this.closeRack(); this.mode = 'walk'; this.d.input.yaw = SEATS[had?.seat ?? 0].yaw + Math.PI; }
      return;
    }
    this.leavers.delete(pid);
    this.sitters.set(pid, { seat, since: this.t, fx: 0, fy: 0, fz: 0, fyaw: 0, captured: false });
    if (pid !== this.myId) return;
    const s = SEATS[seat];
    this.mode = 'seated';
    this.pushT = 0; this.stickArmed = false;
    // the seat camera starts behind your shoulders at the bar; in a booth it starts out in the aisle
    // looking in at you (behind you is the bench back)
    this.d.input.yaw = s.bar ? s.yaw : Math.PI / 2; this.d.input.pitch = -0.12;
    if (s.bar && !this.hotdogPlaying) this.startTalk(true);
  }

  // ---------------------------------------------------------------- input (per sim tick)
  filterInput(f: InputFrame) {
    const pressed = f.buttons & ~this.prevButtons;
    this.prevButtons = f.buttons;
    if (this.stageNow === RunStage.Launch) {
      f.mx = 0; f.my = 0; f.buttons = 0; f.yaw = -Math.PI / 2; f.pitch = 0;
      return;
    }
    if (this.stageNow === RunStage.Run) {
      // falling in: no flying off before the landing, the guns are free once you're down
      if (this.dropAt >= 0 && this.landedAt < 0) f.buttons &= ~(Btn.Fly | Btn.Jump | Btn.Dodge);
      return;
    }
    // saloon
    f.buttons &= ~SALOON_STRIP;
    const seat = this.mySeat();
    if (seat >= 0) {
      const s = this.sitters.get(this.myId)!;
      const idle = this.mode === 'seated' && !(this.hotdogPlaying && this.t < HOTDOG_LOCK) && this.t - s.since > 0.6;
      if (idle) {
        // up off the seat: ○, or push the stick and hold it a beat (a nudge shouldn't stand you up)
        const push = Math.hypot(f.mx, f.my);
        if (push < 0.3) this.stickArmed = true;
        this.pushT = this.stickArmed && push > 0.6 ? this.pushT + 1 / 60 : 0;
        if ((pressed & Btn.Dodge) || this.pushT > 0.15) { this.pushT = 0; this.d.session.sit(NO_SEAT); }
        else if ((pressed & Btn.Interact) && SEATS[seat].bar) this.startTalk(false);
      }
      f.mx = 0; f.my = 0; f.buttons = 0;
      return;
    }
    if (this.mode === 'rack' || this.mode === 'paper' || (this.hotdogPlaying && this.t < HOTDOG_LOCK)) { f.mx = 0; f.my = 0; f.buttons = 0; return; }
    // asked for a seat: stand still until the server seats you, so the walk doesn't carry you past it
    // (and on to the next stool's prompt) while the request is on the wire
    if (this.t - this.sitAsked < 0.6) { f.mx = 0; f.my = 0; }
    if (pressed & Btn.Interact) {
      if (this.nearSeat >= 0 && this.t - this.sitAsked > 0.5) { this.sitAsked = this.t; this.d.session.sit(this.nearSeat); }
      else if (this.nearRack) this.openRack('walk');
      f.buttons &= ~Btn.Interact;
    }
  }

  // ---------------------------------------------------------------- the conversation
  /** Pop says a line: caption + lip-sync + voice. Returns how long he's talking. */
  private popSay(line: string, tag?: string, hold = 1.2): number {
    const secs = this.pop.say(line);
    this.ui.caption(line, 'Pop', Math.max(hold, secs + 0.6));
    this.d.sfx.say?.(line, tag);
    this.popLineUntil = this.t + Math.max(line.length * 0.026 + 0.3, secs);
    return secs;
  }

  /** a caption from Pop across the room, in his voice */
  private popCaption(line: string, hold: number, tag?: string) {
    const secs = this.pop.say(line);
    this.ui.caption(line, 'Pop', Math.max(hold, secs + 0.6));
    this.d.sfx.say?.(line, tag);
  }

  /** you sat at the bar (auto) or pressed □ on your stool: Pop comes over */
  private startTalk(auto: boolean) {
    if (this.mode === 'talk') return;
    this.mode = 'talk';
    this.camCut = true;
    this.talkShot = 'pop';
    this.d.input.unlock(); this.d.input.blocked = true;
    this.pop.lookAt = new THREE.Vector3();
    let first = false;
    try { first = !localStorage.getItem('emg.metPop'); localStorage.setItem('emg.metPop', '1'); } catch { /* private mode */ }
    const hello = first ? L.GREETING_FIRST : L.next(L.GREETINGS);
    // he has to come down the bar first if you sat far from him
    const s = SEATS[this.mySeat()];
    const walk = s ? Math.min(1.6, Math.abs(this.pop.group.position.z - s.z) * 0.5) : 0;
    this.later(auto ? 0.35 + walk : 0.15, () => { this.popSay(hello, 'lean'); this.offerReplies(); });
  }

  private later(secs: number, run: () => void) { this.queued = { at: this.t + secs, run }; }

  /** the replies come up once Pop has finished his line */
  private offerReplies() {
    if (this.mode !== 'talk') return;
    this.ui.closeChoices();
    // the replies come up while he's still finishing (never more than a beat), so you're never stuck
    // waiting on Pop with nothing to press
    const wait = Math.max(0.2, Math.min(0.8, this.popLineUntil - this.t));
    this.later(wait, () => this.showReplies());
  }

  private showReplies() {
    if (this.mode !== 'talk') return;
    this.talkShot = 'pop';
    const rows: Row[] = [];
    rows.push(this.myReady ? { label: L.YOU.unready, act: () => this.reply(L.YOU.unready, () => this.saddle(false)) }
      : { label: this.readyNeed > 1 ? `${L.YOU.saddle} (${this.readyN}/${this.readyNeed})` : L.YOU.saddle, act: () => this.reply(L.YOU.saddle, () => this.saddle(true)) });
    rows.push({ label: L.YOU.rack, act: () => this.reply(L.YOU.rack, () => this.pointToRack()) });
    rows.push({ label: L.YOU.job, act: () => this.reply(L.YOU.job, () => { this.popSay(L.JOB[this.jobIx % L.JOB.length]); this.jobIx++; this.offerReplies(); }) });
    rows.push({ label: L.YOU.pour, act: () => this.reply(L.YOU.pour, () => {
      this.popSay(Math.random() < 0.3 ? L.next(L.POSSE) : L.next(L.POUR));
      this.d.sfx.play('hotdog_slide', { x: wx(CAR.barX0), y: wy(CAR.barTop), z: this.pop.group.position.z + SALOON.z }, { volume: 0.35, pitch: 1.4 });
      this.offerReplies();
    }) });
    if (paperwork.open) rows.push({ label: L.YOU.paper, act: () => this.reply(L.YOU.paper, () => { const s = this.popSay(L.next(L.PAPERWORK)); this.later(Math.min(1.6, s), () => this.openPaperwork()); }) });
    this.ui.glyphs = this.d.input.glyphs;
    this.ui.choices(rows, () => this.endTalk(false));
  }

  /** your cowboy says the line (the camera turns to you), then Pop answers */
  private reply(line: string, then: () => void) {
    this.ui.closeChoices();
    this.ui.caption(line, this.d.myName?.() ?? 'You', 0.6, true);
    this.talkShot = 'you'; this.camCut = true;
    this.later(0.55 + line.length * 0.03, () => { this.talkShot = 'pop'; this.camCut = true; then(); });
  }

  private endTalk(silent: boolean) {
    this.queued = null;
    this.ui.closeChoices();
    if (this.mode !== 'talk') return;
    this.mode = this.mySeat() >= 0 ? 'seated' : 'walk';
    this.stickArmed = false; this.pushT = 0;
    this.pop.lookAt = null;
    this.d.input.blocked = false;
    this.d.input.swallowPad();
    if (this.d.input.glyphs !== 'pad') this.d.input.lock();
    if (!silent) this.popCaption(L.next(L.FAREWELL), 2.4, 'still');
  }

  private saddle(on: boolean) {
    this.myReady = on;
    this.d.session.setReady(on);
    if (on) {
      const others = this.readyNeed - this.readyN - 1;
      this.popSay(others > 0 ? L.next(L.WAITING) : L.next(L.SADDLE));
      this.d.sfx.play('notice', undefined, { volume: 0.4 });
    } else this.popSay(L.next(L.UNREADY));
    this.offerReplies();
  }

  /** "Show me the rack.": Pop points over your shoulder, the camera goes over to the rack */
  private pointToRack() {
    this.pop.point(v.set(wx(CAR.hw - 0.3), wy(1.5), wz(CAR.rackZ)), 2.6);
    this.pop.lookAt = new THREE.Vector3(wx(CAR.hw - 0.3), wy(1.6), wz(CAR.rackZ));
    this.popSay(L.next(L.RACK_POINT), 'point');
    this.later(1.1, () => this.openRack('talk'));
  }

  // ---------------------------------------------------------------- the rack
  private openRack(back: Mode) {
    if (this.mode === 'rack') return;
    this.ui.closeChoices();
    this.queued = null;
    this.rackBack = back;
    this.rackFrom = this.t;
    this.mode = 'rack';
    this.d.input.unlock(); this.d.input.blocked = true;
    const kit = this.d.kit;
    const cls = kit.cls();
    this.rackUI.glyphs = this.d.input.glyphs;
    this.rackUI.show(cls, kit.loadout(cls), {
      browse: (kind, id) => this.car.props.focus(kind, id),
      equipClass: (c) => { kit.setClass(c); this.rackUI.setKit(c, kit.loadout(c)); this.showEquipped(); },
      equipLoadout: (l) => { kit.setLoadout(kit.cls(), l); this.showEquipped(); },
      close: (changed) => this.rackClosed(changed),
      sound: (n) => this.d.sfx.play(n, undefined, { volume: n === 'ui_move' ? 0.35 : 0.5 }),
    });
  }

  private closeRack() { if (this.rackUI.open) this.rackUI.close(); }

  /** your class and kit sit in a brighter light on the rack (checked once a second while aboard) */
  private eqAt = 0;
  private showEquipped() {
    const kit = this.d.kit, c = kit.cls(), lo = kit.loadout(c);
    this.car.props.setEquipped(c, [lo.primary, lo.secondary], lo.grenade);
  }

  private rackClosed(changed: boolean) {
    this.car.props.focus(null);
    if (this.mode !== 'rack') return;
    this.camCut = false;
    if (this.rackBack === 'talk' && this.mySeat() >= 0) {
      this.mode = 'talk';
      this.talkShot = 'pop';
      this.pop.lookAt = new THREE.Vector3();
      this.later(0.15, () => { this.popSay(changed ? L.next(L.LOADOUT_DONE) : L.next(L.FAREWELL), 'lean'); this.offerReplies(); });
      return;
    }
    this.mode = this.mySeat() >= 0 ? 'seated' : 'walk';
    // on foot you were nose to the cabinet: come back facing down the aisle, the camera behind you
    if (this.mode === 'walk') { this.d.input.yaw = Math.PI / 2; this.d.input.pitch = -0.08; }
    this.stickArmed = false;
    this.d.input.blocked = false;
    this.d.input.swallowPad();
    if (this.d.input.glyphs !== 'pad') this.d.input.lock();
    if (changed) this.popCaption(L.next(L.LOADOUT_DONE), 3.2, 'lean');
  }

  /** the Service Record: wardrobe, commendations, and contracts to sign for the next ride */
  private openPaperwork() {
    if (!paperwork.open || this.mode !== 'talk') return;
    this.ui.closeChoices();
    this.mode = 'paper';
    this.d.input.blocked = false; // the record runs its own input through the menu nav
    this.d.input.unlock();
    paperwork.open(() => {
      if (this.mode !== 'paper') return;
      this.mode = 'talk';
      this.d.input.blocked = true;
      this.d.input.swallowPad();
      this.later(0.3, () => { this.popSay(L.next(L.FAREWELL)); this.offerReplies(); });
    });
  }

  // ---------------------------------------------------------------- the room's look
  /** indoor light: the sun becomes the skylight key, the HUD steps aside. Restored for the run. */
  private applyLook(indoor: boolean) {
    const st = this.d.stage;
    const u = st.post.material.uniforms;
    if (!this.lit.saved) {
      this.lit.sun = st.sun.intensity; this.lit.hemi = st.hemi.intensity;
      this.lit.sunColor.copy(st.sun.color); this.lit.hemiSky.copy(st.hemi.color); this.lit.hemiGround.copy(st.hemi.groundColor);
      this.lit.hud = u.uHasHud.value as number; this.lit.shafts = u.uShafts.value as number;
      this.lit.saved = true;
    }
    if (indoor) {
      st.sun.intensity = 1.5; st.sun.color.setRGB(1.0, 0.8, 0.58);
      // the car's light is baked: a sun shadow pass would redraw the whole room for nothing. The pass
      // is skipped and the shadow faded out rather than castShadow switched off: the shadow count is
      // part of every lit shader, so toggling it recompiled the whole scene at the blast-off
      st.sun.shadow.autoUpdate = false; st.sun.shadow.intensity = 0;
      st.hemi.intensity = 0.55; st.hemi.color.setRGB(1.0, 0.72, 0.45); st.hemi.groundColor.setRGB(0.32, 0.17, 0.09);
      u.uShafts.value = 0;
    } else {
      st.sun.intensity = this.lit.sun; st.sun.color.copy(this.lit.sunColor);
      st.sun.shadow.autoUpdate = true; st.sun.shadow.intensity = 1;
      st.hemi.intensity = this.lit.hemi; st.hemi.color.copy(this.lit.hemiSky); st.hemi.groundColor.copy(this.lit.hemiGround);
      u.uShafts.value = this.lit.shafts;
    }
  }
  private indoor = true;
  /** the island is on screen (false while the car's interior hides it) */
  get worldShown() { return !this.indoor; }

  // ---------------------------------------------------------------- per frame
  /**
   * After the game's own camera update. Bends the cowboy render states and may take the camera.
   * Returns true when the HUD should be hidden.
   */
  frame(dt: number, time: number, me: PlayerState | null, px: number, py: number, pz: number, rs: CowboyRS[], n: number): boolean {
    this.t += dt;
    this.postFx.flash = Math.max(0, this.postFx.flash - dt * 2.5);
    this.postFx.desat = 0;
    const st = this.d.stage;
    const cam = st.camera;
    this.ui.glyphs = this.d.input.glyphs;
    this.rackUI.glyphs = this.d.input.glyphs;
    this.ui.update(dt);
    this.rackUI.update();
    if (this.aboard && this.t >= this.eqAt) { this.eqAt = this.t + 1; this.showEquipped(); }
    this.pop.update(dt, time);
    if (this.queued && this.t >= this.queued.at) { const q = this.queued; this.queued = null; q.run(); }

    // the car only exists while we're aboard (and for the first seconds of the drop, out of sight anyway)
    const aboard = this.aboard;
    this.car.group.visible = aboard;
    const wantIndoor = aboard && !(this.stageNow === RunStage.Launch && this.t > 5.6);
    if (wantIndoor !== this.indoor) { this.indoor = wantIndoor; this.applyLook(wantIndoor); this.car.setIndoor(wantIndoor); this.d.setWorldVisible?.(!wantIndoor); }
    if (wantIndoor) {
      // the key light comes down through the skylight
      st.sun.target.position.set(px, py, pz);
      st.sun.position.set(px + 4, py + 30, pz + 3);
    }

    // render states are reused frame to frame: clear last frame's scripted pose; guns stay holstered aboard
    for (let i = 0; i < n; i++) { rs[i].scripted = 0; rs[i].holstered = aboard; rs[i].reachW = 0; rs[i].headDip = 0; }

    if (this.stageNow === RunStage.Saloon) this.poseSitters(rs, n);

    let hideHud = aboard;
    let camWant = false;
    if (this.stageNow === RunStage.Saloon) camWant = this.frameSaloon(dt, me, px, py, pz);
    else if (this.stageNow === RunStage.Launch) this.frameLaunch(dt, rs, n, cam);
    else this.frameDrop(dt, me, rs, n, px, py, pz);
    if (this.cinematic) hideHud = true;
    if (!this.hotdogPlaying && this.stageNow === RunStage.Saloon) { this.applyCam(dt, camWant); this.peekCam(dt); }
    else this.camK = 0;
    if (this.mode === 'rack') { this.clearRackShot(rs, n); this.rackBracket(); }

    this.car.update(dt, time, this.rumble, cam);
    // pop looks at the talker
    if (this.pop.lookAt && me && (this.mode === 'talk' || this.hotdogPlaying)) {
      const s = this.sitters.get(this.myId);
      if (s) this.pop.lookAt.set(wx(SEATS[s.seat].x), wy(SEATS[s.seat].top + 0.72), wz(SEATS[s.seat].z));
      else this.pop.lookAt.set(px, py + 1.6, pz);
    }
    return hideHud;
  }

  /** everyone on a seat is drawn on it (sat pose, facing the bar or the table); getting on and off blends */
  private poseSitters(rs: CowboyRS[], n: number) {
    for (let i = 0; i < n; i++) {
      const r = rs[i];
      const s = this.sitters.get(r.id);
      if (s) {
        const seat = SEATS[s.seat];
        if (!s.captured) { s.captured = true; s.fx = r.x; s.fy = r.y; s.fz = r.z; s.fyaw = r.yaw; }
        const k = smooth((this.t - s.since) / SIT_BLEND);
        r.x = lerp(s.fx, wx(seat.x), k); r.z = lerp(s.fz, wz(seat.z), k);
        r.y = lerp(s.fy, wy(seat.top - SEAT_DROP), k);
        r.yaw = s.fyaw + angDiff(s.fyaw, seat.yaw) * k;
        r.vx = r.vy = r.vz = 0; r.move = Move.Ground; r.grounded = true;
        // the wipe return: your own hand goes for the frank in front of you (eatPose); the others at
        // the bar eat theirs off-screen (the eat clip), sat otherwise
        r.scripted = this.hotdogPlaying && !r.isLocal ? 2 : 1;
        r.scriptedT = this.t - s.since;
        if (r.isLocal && this.reason === SaloonReason.Wipe && this.hotdog.plate.visible && this.eatPose()) {
          const e = this.eat;
          r.reachW = e.w; r.reachX = e.x; r.reachY = e.y; r.reachZ = e.z; r.headDip = e.dip; r.fist = e.fist;
        }
        continue;
      }
      const l = this.leavers.get(r.id);
      if (l) {
        const k = smooth((this.t - l.since) / SIT_BLEND);
        if (k >= 1) { this.leavers.delete(r.id); continue; }
        const seat = SEATS[l.seat];
        r.x = lerp(wx(seat.x), r.x, k); r.z = lerp(wz(seat.z), r.z, k); r.y = lerp(wy(seat.top - SEAT_DROP), r.y, k);
      }
    }
  }

  // ---------------------------------------------------------------- saloon
  /** returns true when the director wants the camera */
  private frameSaloon(dt: number, me: PlayerState | null, px: number, py: number, pz: number): boolean {
    this.rumble = Math.max(0, this.rumble - dt);
    const lx = px - SALOON.x, lz = pz - SALOON.z;
    const inCar = me ? inSaloon(px, py, pz) : false;
    const mySeat = this.mySeat();
    // Pop works the bar where you sit
    if (mySeat >= 0 && SEATS[mySeat].bar) this.pop.homeZ = popSpot(SEATS[mySeat].z);

    // what's within reach (standing)
    this.nearSeat = -1; this.nearRack = false;
    if (inCar && mySeat < 0 && this.mode === 'walk' && !this.hotdogPlaying) {
      let best = SEAT_REACH;
      const taken = new Set<number>();
      for (const s of this.sitters.values()) taken.add(s.seat);
      for (let i = 0; i < SEATS.length; i++) {
        if (taken.has(i)) continue;
        const s = SEATS[i];
        // reach a booth seat from the aisle end of its bench; a stool from anywhere round it
        const d = s.bar ? Math.hypot(lx - s.x, lz - s.z) : Math.hypot(Math.max(0, lx - (-1.2)) * 0.6 + Math.abs(lx - s.x) * 0.4, lz - s.z);
        if (d < best) { best = d; this.nearSeat = i; }
      }
      if (this.nearSeat < 0 && lx > CAR.hw - 1.9 && Math.abs(lz - CAR.rackZ) < RACK_R) this.nearRack = true;
    }
    if (this.nearSeat >= 0) this.ui.setPrompt('square', SEATS[this.nearSeat].bar ? 'Take a stool' : 'Sit down');
    else if (this.nearRack) this.ui.setPrompt('square', 'Gun rack');
    else this.ui.setPrompt(null, '');
    // the hints along the bottom: what you can do from your seat
    let hints: Hint[] | null = null;
    if (this.mode === 'seated' && !this.hotdogPlaying) {
      hints = [];
      if (SEATS[mySeat]?.bar) hints.push({ g: 'square', key: 'E', text: 'Talk to Pop' });
      hints.push({ g: 'circle', key: 'C', text: 'Stand up' });
    } else if (this.mode === 'seated' && this.t > HOTDOG_LOCK) hints = [{ g: 'circle', key: 'C', text: 'Stand up' }];
    else if (this.mode === 'talk' && this.ui.choosing) hints = [{ g: 'cross', key: 'ENTER', text: 'Say it' }, { g: 'circle', key: 'ESC', text: "That's all" }];
    this.ui.setHints(hints);
    this.ui.captionHigh(this.mode === 'rack');
    this.ui.setReady(!this.hotdogPlaying && this.mode !== 'rack', this.readyN, this.readyNeed, this.myReady, !!SEATS[mySeat]?.bar);

    // first arrival: Pop calls across the room
    if (!this.boarded && me && this.t > 1.2) {
      this.boarded = true;
      let first = false;
      try { first = !localStorage.getItem('emg.boarded'); localStorage.setItem('emg.boarded', '1'); } catch { /* ignore */ }
      if (this.reason === SaloonReason.Board) this.popCaption(first ? L.ARRIVAL_FIRST : L.next(L.GREETINGS), 4);
    }
    // Pop mutters to the room now and then
    this.mutterAt -= dt;
    if (this.mutterAt < 0 && this.mode === 'walk') { this.mutterAt = 30 + Math.random() * 30; this.popCaption(L.next(L.MUTTER), 2.5, 'still'); }

    if (this.hotdogPlaying) { this.hotdogScene(); return false; }
    if (this.reason === SaloonReason.Wipe && this.hotdog.plate.visible && this.t > HOTDOG_SECS + 1 && mySeat < 0) this.hotdog.set(false);

    // the shots
    const seat = SEATS[mySeat];
    if (this.mode === 'rack') return this.rackShot();
    if (!seat) return false;
    const hx = wx(seat.x), hy = wy(seat.top + 0.66), hz = wz(seat.z);
    if (this.mode === 'talk' || this.mode === 'paper') {
      const popZ = this.pop.group.position.z + SALOON.z, popX = wx(CAR.popX - 0.07);
      if (this.talkShot === 'pop') {
        // the two-shot: from the aisle, a step fore of your stool, you in the near third and Pop across the bar
        this.wantPos.set(hx - 1.3, hy + 0.3, hz - 1.2);
        this.wantLook.set(lerp(hx, popX, 0.55), wy(1.38), lerp(hz, popZ, 0.55) + 0.1);
      } else {
        // the reverse: from behind the bar, past Pop's shoulder, at you
        this.wantPos.set(wx(CAR.barX1 + 0.05), wy(1.5), popZ - 0.75);
        this.wantLook.set(hx, hy + 0.05, hz);
      }
      return true;
    }
    // sat, looking round: a short boom over your shoulder, aimed with the right stick / mouse
    const yaw = this.d.input.yaw;
    const pitch = Math.max(-0.7, Math.min(0.45, this.d.input.pitch));
    this.d.input.pitch = pitch;
    const fx = -Math.sin(yaw), fz = -Math.cos(yaw);
    const cp = Math.cos(pitch), sp = Math.sin(pitch);
    // pull the boom in until the camera is out of the booth benches (their backs stand 1.2 m tall)
    let dist = 1.55;
    for (; dist > 0.35; dist -= 0.1) {
      this.wantPos.set(hx - fx * cp * dist + fz * 0.32, hy + 0.12 - sp * dist, hz - fz * cp * dist - fx * 0.32);
      if (!inBench(this.wantPos.x - SALOON.x, this.wantPos.y - SALOON.y, this.wantPos.z - SALOON.z)) break;
    }
    this.wantPos.x = Math.max(wx(-CAR.hw + 0.2), Math.min(wx(CAR.hw - 0.2), this.wantPos.x));
    this.wantPos.z = Math.max(wz(-CAR.hl + 0.25), Math.min(wz(CAR.hl - 0.25), this.wantPos.z));
    this.wantPos.y = Math.max(wy(0.5), Math.min(wy(3.1), this.wantPos.y));
    this.wantLook.set(hx + fx * cp * 2, hy + 0.1 + sp * 2, hz + fz * cp * 2);
    this.camCut = this.camK < 0.01;
    return true;
  }

  /** the whole rack in frame from the aisle, drifting toward the item the readout is on. The readout
   *  has a column down the left, so the rack is framed in the space to the right of it. */
  private rackShot(): boolean {
    const b = this.rackUI.browsing;
    const obj = this.car.props.rackItem(b.kind, b.id);
    const c = v2;
    if (obj) { _box.setFromObject(obj); _box.getCenter(c); }
    else c.set(wx(CAR.hw - 0.3), wy(1.6), wz(CAR.rackZ));
    const rx = wx(CAR.hw - 0.3), ry = wy(1.55), rz = wz(CAR.rackZ);
    const dist = 2.7;
    // where the rack's middle should land across the screen (screen right is +z from the aisle)
    const cam = this.d.stage.camera;
    const w = window.innerWidth;
    const ndc = w > 720 ? this.rackUI.columnRight / w : 0; // the middle of the space right of the column, in -1..1
    const shift = ndc * Math.tan(THREE.MathUtils.degToRad(cam.fov / 2)) * cam.aspect * dist;
    const lookX = lerp(rx, c.x, 0.35), lookY = lerp(ry, c.y, 0.3), lookZ = lerp(rz, c.z, 0.35) - shift;
    this.wantPos.set(rx - dist, lerp(wy(1.5), c.y, 0.2) + 0.05, lerp(rz, c.z, 0.35) - shift);
    this.wantLook.set(lookX, lookY, lookZ);
    return true;
  }

  /**
   * Nobody stands between the lens and the guns: any cowboy (you, walking up to it, or a posse mate
   * browsing next to you) whose body covers the rack on screen and is nearer than it isn't drawn while
   * the rack is open. Local to this screen; everyone else still sees them.
   */
  private clearRackShot(rs: CowboyRS[], n: number) {
    const cam = this.d.stage.camera;
    cam.updateMatrixWorld();
    // the whole case, wall to cabinet front, floor to the class badges
    _box.min.set(wx(CAR.hw - 0.5), wy(0.3), wz(CAR.rackZ - CAR.rackHalf));
    _box.max.set(wx(CAR.hw), wy(2.8), wz(CAR.rackZ + CAR.rackHalf));
    const rack = screenRect(_box, cam);
    if (!rack) return;
    const rackD = cam.position.distanceTo(_box.getCenter(v2));
    for (let i = 0; i < n; i++) {
      const r = rs[i];
      if (r.hidden) continue;
      _box.min.set(r.x - 0.45, r.y, r.z - 0.45);
      _box.max.set(r.x + 0.45, r.y + 2.0, r.z + 0.45);
      const b = screenRect(_box, cam);
      // behind the lens counts too: it's right in your face on the way over
      const inFront = !b || (b.x1 > rack.x0 && b.x0 < rack.x1 && b.y1 > rack.y0 && b.y0 < rack.y1);
      if (inFront && cam.position.distanceTo(v.set(r.x, r.y + 1, r.z)) < rackD) r.hidden = true;
    }
  }

  /** brackets on the browsed rack item, once the camera has got there (after the camera is placed) */
  private rackBracket() {
    const b = this.rackUI.browsing;
    const obj = this.car.props.rackItem(b.kind, b.id);
    if (!obj || this.t - this.rackFrom < 0.5) { this.rackUI.bracket(null); return; }
    const cam = this.d.stage.camera;
    cam.updateMatrixWorld();
    _box.setFromObject(obj);
    this.rackUI.bracket(screenRect(_box, cam));
  }

  /**
   * Sat at the bar talking to Pop, the right stick still looks around a little (James, 2026-10-23): the
   * shot turns up to ~35° either way and ~20° up or down while the stick is held, and settles back to the
   * framing when it's let go. Read straight off the pad: the game's own look input is off while the
   * replies have the buttons.
   */
  private peekCam(dt: number) {
    let wy = 0, wp = 0;
    if ((this.mode === 'talk' || this.mode === 'paper') && this.camK > 0) {
      const gp = firstPad();
      const x = gp?.axes[2] ?? 0, y = gp?.axes[3] ?? 0, m = Math.hypot(x, y);
      if (m > 0.12) { const k = (m - 0.12) / 0.88 / m; wy = -x * k * 0.6; wp = -y * k * 0.35 * (this.d.input.settings.invertY ? -1 : 1); }
    }
    const k = 1 - Math.exp(-dt * 6);
    this.peek.yaw += (wy - this.peek.yaw) * k; this.peek.pitch += (wp - this.peek.pitch) * k;
    if (Math.abs(this.peek.yaw) + Math.abs(this.peek.pitch) < 1e-4 || this.camK <= 0) return;
    const cam = this.d.stage.camera, w = smooth(this.camK);
    cam.rotateOnWorldAxis(Y_UP, this.peek.yaw * w);
    cam.rotateX(this.peek.pitch * w);
  }

  /** ease the director's camera to the shot it wants, blended over the game's own */
  private applyCam(dt: number, want: boolean) {
    const cam = this.d.stage.camera;
    if (want && this.camK < 0.001) {
      // taking over: start from wherever the game's camera is
      this.camPos.copy(cam.position);
      cam.getWorldDirection(this.camLook).multiplyScalar(3).add(cam.position);
    }
    this.camK = Math.max(0, Math.min(1, this.camK + (want ? dt * 3 : -dt * 3)));
    if (this.camK <= 0) return;
    if (want) {
      if (this.camCut && this.mode === 'talk') { this.camPos.copy(this.wantPos); this.camLook.copy(this.wantLook); }
      else {
        const k = 1 - Math.exp(-dt * (this.mode === 'seated' ? 14 : this.mode === 'rack' ? 5 : 3.2));
        this.camPos.lerp(this.wantPos, k); this.camLook.lerp(this.wantLook, k);
      }
      this.camCut = false;
    }
    const k = smooth(this.camK);
    v.copy(cam.position).lerp(this.camPos, k);
    cam.getWorldDirection(v2).multiplyScalar(3).add(cam.position).lerp(this.camLook, k);
    cam.position.copy(v);
    cam.lookAt(v2);
  }

  /**
   * After the cowboys are posed this frame (game.ts): the frank follows the hand that's holding it,
   * and the hotdog close-up frames the face. Both want this frame's bones, not last frame's.
   */
  afterCowboys(dt: number) {
    if (this.stageNow !== RunStage.Saloon || this.reason !== SaloonReason.Wipe || !this.hotdog.plate.visible) return;
    this.updateHotdog(dt);
    if (this.hotdogPlaying) { this.settleMouth(); this.hotdogCam(dt); }
  }

  /** where you eat it: in front of the mouth, from the head bone (the mask hides the mouth itself) */
  /** findMouth(), or the stool's guess while your cowboy isn't drawn on it yet */
  private settleMouth() {
    // the head bone's origin doesn't move when the head dips, so the hand can chase it frame to frame
    const seat = SEATS[this.mySeat() >= 0 ? this.mySeat() : wipeStool(this.d.session.slot)];
    if (!this.findMouth() || Math.abs(this.mouthP.x - wx(seat.x)) + Math.abs(this.mouthP.z - wz(seat.z)) > 1) {
      this.mouthP.set(wx(seat.x) + 0.15, wy(seat.top) + 0.86, wz(seat.z) + 0.02);
    }
  }

  private findMouth(): boolean {
    if (!this.d.boneWorld?.(this.myId, 'Head', this.headM)) return false;
    this.headM.decompose(this.headP, _hq, _hs);
    // seated at the bar you face +X
    this.mouthP.copy(this.headP).add(v.set(0.1, 0.07, 0));
    return true;
  }

  /**
   * Your right hand through the hotdog, by the clock: reach for the plate, pick it up, hold it at the
   * chest, three bites (up to the mouth, the head dips to meet it, a moment, back down), then it
   * goes back on the plate. Fills this.eat; false when the hand is its own again.
   */
  private eatPose(): boolean {
    const t = this.t, e = this.eat;
    if (t < EAT_REACH || t > EAT_DONE + 0.5 || this.mySeat() < 0) { e.w = 0; return false; }
    this.settleMouth();
    const m = this.mouthP;
    // the frank on the plate: the wrist comes in behind it and a little above
    this.hotdog.plateWorld(this.plateP).add(v.set(-0.07, 0.05, 0.03));
    // held: low in front of the chest, a touch to the right
    this.holdP.copy(m).add(v.set(0.24, -0.25, 0.1));
    // at the mouth: the wrist a hand and a frank's length in front of it (the nose goes in ~2 cm)
    this.biteP.copy(m).add(v.set(0.2, -0.07, 0.06));
    let lift = 0, dip = 0;
    const p = v2;
    if (t < EAT_GRAB) p.copy(this.plateP);
    else if (t < EAT_HELD) p.lerpVectors(this.plateP, this.holdP, smooth((t - EAT_GRAB) / (EAT_HELD - EAT_GRAB)));
    else if (t < EAT_BACK) {
      p.copy(this.holdP);
      for (const b of EAT_BITES) {
        const up = smooth((t - (b - EAT_UP)) / EAT_UP) * (1 - smooth((t - (b + 0.18)) / EAT_DOWN));
        if (up > lift) lift = up;
        // the head comes down to meet it, then chews
        const since = t - b;
        if (since > -0.25 && since < 1.4) dip = Math.max(dip, since < 0 ? smooth((since + 0.25) / 0.25) : (1 - smooth(since / 0.5)) * 0.8 + Math.max(0, Math.sin(since * 14)) * 0.18 * (1 - since / 1.4));
      }
      p.lerp(this.biteP, lift);
    } else p.lerpVectors(this.holdP, this.plateP, smooth((t - EAT_BACK) / (EAT_DONE - EAT_BACK - 0.2)));
    e.x = p.x; e.y = p.y; e.z = p.z; e.lift = lift; e.dip = dip;
    e.fist = smooth((t - (EAT_GRAB - 0.15)) / 0.2) * (1 - smooth((t - (EAT_DONE - 0.3)) / 0.2));
    // the arm comes in at the start and goes back to the bar at the end
    e.w = smooth((t - EAT_REACH) / 0.45) * (1 - smooth((t - EAT_DONE) / 0.45));
    return true;
  }

  /** the frank: on the plate, into the hand, bitten (the wipe return, while you stay sat) */
  private updateHotdog(dt: number) {
    const id = this.myId, t = this.t;
    if (this.mySeat() < 0 && !this.hotdogPlaying) return;
    if (t > EAT_GRAB && t < EAT_DONE - 0.25 && !this.picked) { this.picked = true; this.hotdog.pickUp(); }
    if (t > EAT_DONE - 0.25 && this.picked) { this.picked = false; this.hotdog.putDown(); }
    while (this.bitesDone < EAT_BITES.length && t > EAT_BITES[this.bitesDone] && this.picked) {
      this.bitesDone++;
      this.hotdog.bite();
      this.biteSound();
      if (this.bitesDone === 1 && !this.welcomed) {
        // he lets you have the first bite before he says anything
        this.welcomed = true;
        this.later(1.0, () => { this.popCaption(L.WIPE_RETURN, 2.6, 'shrug'); this.pop.lookAt = new THREE.Vector3(); });
      }
    }
    const ok = this.d.boneWorld?.(id, 'RightHand', this.handM) && this.d.boneWorld?.(id, 'RightForeArm', this.elbowM);
    if (ok) { this.wristP.setFromMatrixPosition(this.handM); this.elbowP.setFromMatrixPosition(this.elbowM); }
    this.hotdog.update(dt, ok ? this.wristP : null, ok ? this.elbowP : null, this.mouthP, this.eat.lift);
  }

  private hotdogScene() {
    const t = this.t;
    const s = SEATS[wipeStool(this.d.session.slot)];
    const sx = wx(s.x), sz = wz(s.z), top = wy(s.top);
    this.ui.fade(Math.max(0, 1 - t / 1.2));
    // the wipe's greyscale bleeds back into colour
    this.postFx.desat = Math.max(0, 1 - t / 2.6);
    if (t > 0.5 && !this.hotdogSaid) {
      this.hotdogSaid = true;
      this.d.sfx.play('hotdog_slide', { x: sx + 0.4, y: top + 0.4, z: sz }, { volume: 0.7 });
    }
    if (!this.welcomed && t > 6.2) { this.welcomed = true; this.popCaption(L.WIPE_RETURN, 2.6, 'shrug'); this.pop.lookAt = new THREE.Vector3(); }
    if (t > HOTDOG_SECS - 1 && this.pop.lookAt) this.pop.lookAt = null;
  }

  /**
   * The close-up: across the bar, off your right shoulder line (the beer towers stand to the left of the wipe stools), at mouth height, so the frank
   * comes up into frame between the lens and the face. A slow push in and a slight drift.
   */
  private hotdogCam(dt: number) {
    const t = this.t;
    const cam = this.d.stage.camera;
    const s = SEATS[wipeStool(this.d.session.slot)];
    // framed on the mouth, eased so the idle sway doesn't shake the lens
    if (this.camMouthT < 0 || t < this.camMouthT) this.camMouth.copy(this.mouthP);
    else this.camMouth.lerp(this.mouthP, Math.min(1, dt * 3));
    this.camMouthT = t;
    const m = this.camMouth;
    const push = smooth(t / HOTDOG_SECS);
    const fwd = 1.25 - push * 0.3, side = 0.42 - push * 0.1;
    cam.position.set(m.x + fwd, m.y + 0.06 + Math.sin(t * 0.6) * 0.008, m.z + side + Math.sin(t * 0.37) * 0.01);
    cam.lookAt(m.x + 0.1, m.y - 0.1, m.z + 0.05);
    cam.fov = t > HOTDOG_SECS - 0.1 ? 62 : 34;
    cam.updateProjectionMatrix();
  }

  /** a bite: there's no recording for it, so a short wet crunch of filtered noise */
  private biteSound() {
    const ctx = (this.d.sfx as unknown as { ctx?: AudioContext | null }).ctx;
    if (!ctx) return;
    const now = ctx.currentTime;
    const len = 0.22;
    const buf = ctx.createBuffer(1, Math.floor(ctx.sampleRate * len), ctx.sampleRate);
    const ch = buf.getChannelData(0);
    for (let i = 0; i < ch.length; i++) {
      const u = i / ch.length;
      // crackle: sparse spikes over a soft bed, falling off fast
      ch[i] = ((Math.random() * 2 - 1) * 0.35 + (Math.random() < 0.04 ? (Math.random() * 2 - 1) : 0)) * Math.pow(1 - u, 2.2);
    }
    const src = ctx.createBufferSource(); src.buffer = buf;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass'; bp.frequency.value = 1700; bp.Q.value = 0.8;
    const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, now); g.gain.exponentialRampToValueAtTime(0.32, now + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, now + len);
    src.connect(bp).connect(g).connect(ctx.destination);
    src.start(now);
  }

  // ---------------------------------------------------------------- the launch
  private frameLaunch(dt: number, rs: CowboyRS[], n: number, cam: THREE.PerspectiveCamera) {
    const t = this.t;
    const ox = SALOON.x, oy = SALOON.y, oz = SALOON.z;
    const fx = this.d.fx();
    this.ui.setPrompt(null, ''); this.ui.setHints(null);
    this.ui.setReady(false, 0, 0, false);
    // black dip into the scene, flash out of it
    this.ui.fade(t < 0.35 ? 1 - t / 0.35 : t > LAUNCH_SECS - 0.35 ? (t - (LAUNCH_SECS - 0.35)) / 0.35 : 0);
    if (!this.launchSaid && t > 0.5) { this.launchSaid = true; this.popCaption(L.next(L.SADDLE), 2.6, 'point'); }
    if (!this.klaxon && t > 1.4) { this.klaxon = true; this.d.sfx.play('service_klaxon', { x: ox, y: oy + 3, z: oz }, { volume: 0.7 }); }
    const IGN = 2.9, LIFT = 4.7, BREACH = 5.05, CUT = 5.6;
    if (!this.ignited && t > IGN) { this.ignited = true; this.d.sfx.play('jetpack_ignite', { x: ox, y: oy + 1, z: oz }, { volume: 1 }); }
    this.rumble = t < IGN ? 0.15 * smooth(t / IGN) : t < CUT ? 0.4 + 0.6 * smooth((t - IGN) / (BREACH - IGN)) : 0;
    if (t > IGN && t < CUT) this.d.shake(dt * (t > LIFT ? 3 : 0.9));
    this.d.sfx.loop('saloon_rumble', 'jetpack', t > IGN && t < CUT + 1 ? 0.5 + this.rumble * 0.5 : 0, { x: ox, y: oy + 1, z: oz }, 0.7);

    // cowboys: lined up, brace, ignite, then up through the skylight
    for (let i = 0; i < n; i++) {
      const r = rs[i];
      const s = launchSpot(r.slot);
      r.x = s.x; r.z = s.z; r.y = s.y; r.yaw = -Math.PI / 2; r.vx = r.vz = 0; r.vy = 0;
      r.scripted = 4; r.scriptedT = t;
      r.move = Move.Ground; r.grounded = true;
      if (t > IGN) { r.move = Move.Hover; r.grounded = false; r.vy = 1; }
      if (t > LIFT) {
        const k = t - LIFT;
        // up through the roof: accelerate hard, a slot-staggered start so they don't move as one
        const kk = Math.max(0, k - r.slot * 0.06);
        r.y = s.y + 0.5 * 40 * kk * kk + kk * 2;
        r.vy = 40 * kk + 2;
        r.move = Move.Flight; r.scripted = 0;
        // drift toward the skylight's centre line as they rise
        r.x = lerp(s.x, ox, smooth(kk / 0.4));
      }
      if (t > CUT) {
        // outside: climbing out of the hole, fanning, then arcing over toward the island
        const k = t - CUT;
        const holeZ = oz + (r.slot - 1.5) * 1.6;
        r.x = ox + (r.slot - 1.5) * 1.2 * k;
        r.y = oy + CAR.skyH + 3 + 26 * k + 4 * k * k;
        r.z = lerp(holeZ, holeZ + 8, smooth(k / 3));
        r.vy = 26 + 8 * k; r.vz = 4;
        r.move = Move.Flight;
      }
      if (fx && t > IGN && t < LAUNCH_SECS) {
        v.set(r.x, r.y + 0.9, r.z);
        fx.jetFlame(v, v2.set(0, -1, 0), t > LIFT ? 1 : 0.5 + 0.3 * Math.sin(t * 30));
      }
    }
    if (fx && t > IGN && t < LIFT && Math.random() < dt * 12) fx.dust(v.set(ox - 0.2 + (Math.random() - 0.5) * 2, oy + 0.05, oz + (Math.random() - 0.5) * 5), 0.8);
    if (!this.breached && t > BREACH) {
      this.breached = true;
      this.car.breach(true);
      this.d.sfx.play('roof_timber', { x: ox, y: oy + 4, z: oz }, { volume: 1 });
      this.d.sfx.play('explosion', { x: ox, y: oy + 4, z: oz }, { volume: 0.8 });
      if (fx) for (let k = -1; k <= 1; k++) fx.explosion(v.set(ox, oy + CAR.skyH, oz + k * 2.2), 2.2, 2);
      this.postFx.flash = 0.9;
    }

    // ---- camera
    if (t < 2.6) {
      // A: low and wide from the booths, the line-up against the bar, Pop behind them
      const k = smooth(t / 2.6);
      cam.position.set(ox - 2.15, oy + 0.95 + k * 0.1, oz + 4.4 - k * 0.6);
      cam.lookAt(ox + 0.6, oy + 1.25, oz - 0.6);
    } else if (t < CUT) {
      // B: on the floor by their boots looking up at the skylight they're about to go through
      const k = smooth((t - 2.6) / (CUT - 2.6));
      cam.position.set(ox - 1.55, oy + 0.4, oz + 2.6);
      cam.lookAt(ox - 0.05, oy + lerp(1.4, 3.9, k), oz - 0.2);
    } else {
      // C: outside the car, the roof below, four trails climbing out of it into the sky
      const k = (t - CUT) / (LAUNCH_SECS - CUT);
      cam.position.set(ox - 11, oy + 9 + k * 6, oz + 16);
      cam.lookAt(ox, oy + 5 + k * 40, oz + 2 * k);
    }
    cam.fov = t < CUT ? 62 : 55; cam.updateProjectionMatrix();
  }

  // ---------------------------------------------------------------- the drop + superhero landing
  private frameDrop(dt: number, me: PlayerState | null, rs: CowboyRS[], n: number, px: number, py: number, pz: number) {
    this.ui.setPrompt(null, ''); this.ui.setHints(null); this.ui.setReady(false, 0, 0, false);
    const cam = this.d.stage.camera;
    if (cam.fov !== this.d.stage.camera.fov) cam.updateProjectionMatrix();
    if (this.dropAt < 0) { this.ui.fade(0); return; }
    this.dropAt += dt;
    this.ui.fade(Math.max(0, 1 - this.dropAt / 0.45));
    if (!me) return;
    if (this.landedAt < 0) {
      // falling: look down at the island coming up
      if (!me.grounded) { this.sawAir = true; this.d.input.pitch = Math.min(this.d.input.pitch, -0.7); }
      // landed: any grounded frame after we've been airborne (a slow frame rate can skip the fall)
      if (me.grounded && this.sawAir) {
        this.landedAt = 0;
        const fx = this.d.fx();
        fx?.dust(v.set(px, py, pz), 1);
        fx?.explosion(v.set(px, py + 0.2, pz), 2.5, 0);
        this.d.shake(0.7);
        this.d.sfx.play('explosion', { x: px, y: py, z: pz }, { volume: 0.55, pitch: 0.7 });
        this.d.input.rumble(0.8, 1, 260);
      }
      if (this.dropAt > DROP_CAM_SECS + 4) this.landedAt = 0; // never strand the camera
    } else {
      this.landedAt += dt;
      // lift the gaze back to the horizon while he rises out of the crouch
      if (this.landedAt < 1.4) this.d.input.pitch += (-0.08 - this.d.input.pitch) * Math.min(1, dt * 3);
      if (this.landedAt > 3) this.dropAt = -1;
    }
    for (let i = 0; i < n; i++) {
      const r = rs[i];
      if (r.isLocal && this.landedAt >= 0 && this.landedAt < 1.6) { r.scripted = 3; r.scriptedT = this.landedAt; }
    }
    void DROP_HEIGHT;
  }
}

/** where Pop stands to serve a stool at car-local z: across from it, but never behind the till or a beer tower */
function popSpot(z: number) {
  if (Math.abs(z - (CAR.barZ1 - 0.55)) < 0.5) return CAR.barZ1 - 1.1;
  for (const t of [-0.9, -4.4]) if (Math.abs(z - t) < 0.45) return z > t ? t + 0.48 : t - 0.48;
  return z;
}

/** car-local point inside a booth bench block or its table (where the seat camera mustn't go) */
function inBench(x: number, y: number, z: number) {
  if (x > CAR.boothX1 + 0.08 || y > 1.28) return false;
  for (const bay of CAR.bays) {
    const d = Math.abs(z - bay);
    if (d > CAR.bayHalf - 0.68 && d < CAR.bayHalf + 0.06) return true;
  }
  return false;
}

function angDiff(a: number, b: number) { const d = b - a; return Math.atan2(Math.sin(d), Math.cos(d)); }

/** a world box's on-screen rect in CSS px, or null when it's behind the camera */
function screenRect(b: THREE.Box3, cam: THREE.Camera) {
  let x0 = 1e9, y0 = 1e9, x1 = -1e9, y1 = -1e9;
  const w = window.innerWidth, h = window.innerHeight;
  for (let i = 0; i < 8; i++) {
    v.set(i & 1 ? b.max.x : b.min.x, i & 2 ? b.max.y : b.min.y, i & 4 ? b.max.z : b.min.z).project(cam);
    if (v.z > 1) return null;
    const sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
    x0 = Math.min(x0, sx); y0 = Math.min(y0, sy); x1 = Math.max(x1, sx); y1 = Math.max(y1, sy);
  }
  void _m4;
  return { x0, y0, x1, y1 };
}

export type { RackKind };
